import { useEffect, useState } from 'react';
import { useBestSellers } from '../../hooks/useBestSellers';
import { useDragScroll } from '../../hooks/useDragScroll';
import { ProductCard } from '../ui/ProductCard';
import { SectionHeading } from './SectionHeading';

const CAROUSEL_ITEM_WIDTH_PX = 240;

function BestSellersSkeleton() {
  return (
    <div className="flex gap-4 overflow-hidden">
      {Array.from({ length: 4 }).map((_, idx) => (
        <div key={idx} className="w-44 sm:w-56 shrink-0">
          <div className="aspect-square rounded-2xl bg-champagne animate-pulse" />
          <div className="h-3 w-3/4 bg-champagne rounded mt-3 animate-pulse" />
          <div className="h-3 w-1/2 bg-champagne rounded mt-2 animate-pulse" />
        </div>
      ))}
    </div>
  );
}

export function BestSellers() {
  const { items, isLoading, error, isEmpty } = useBestSellers(true);
  const {
    ref: scrollRef,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    onMouseEnter,
    onTouchStart,
    onTouchEnd,
    onClickCapture,
  } = useDragScroll<HTMLDivElement>();
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setHasOverflow(el.scrollWidth > el.clientWidth);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [items, scrollRef]);

  const handleScrollPrev = () => {
    scrollRef.current?.scrollBy({ left: -CAROUSEL_ITEM_WIDTH_PX * 2, behavior: 'smooth' });
  };

  const handleScrollNext = () => {
    scrollRef.current?.scrollBy({ left: CAROUSEL_ITEM_WIDTH_PX * 2, behavior: 'smooth' });
  };

  if (error || isEmpty) return null;

  return (
    <div className="max-w-[1600px] mx-auto px-4 pt-10">
      <SectionHeading title="Mais vendidos" to="/catalogo" />

      {isLoading ? (
        <BestSellersSkeleton />
      ) : (
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 scroll-px-4 md:mx-0 md:px-0 md:scroll-px-0 [@media(pointer:coarse)]:[scroll-snap-type:x_proximity] [@media(pointer:fine)]:cursor-grab [@media(pointer:fine)]:select-none [scroll-behavior:auto]"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
            onMouseEnter={onMouseEnter}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onClickCapture={onClickCapture}
          >
            {items.map((item) => (
              <div key={item.id} className="w-44 sm:w-56 shrink-0 snap-start">
                <ProductCard product={item} swipe={false} />
              </div>
            ))}
          </div>
          {hasOverflow && (
            <>
              <button
                type="button"
                onClick={handleScrollPrev}
                aria-label="Anterior"
                className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-10 h-10 rounded-full bg-white border border-champagne shadow-md items-center justify-center text-text-dark hover:bg-champagne transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={handleScrollNext}
                aria-label="Próximo"
                className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-10 h-10 rounded-full bg-white border border-champagne shadow-md items-center justify-center text-text-dark hover:bg-champagne transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
